import { useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Loader2, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { generatePassword } from "@/lib/password";
import { whatsappLink } from "@/lib/billing";

const FIELDS = [
  ["company_name", "Nome da empresa", "text"],
  ["owner_name", "Responsável", "text"],
  ["phone", "WhatsApp do responsável", "text"],
  ["owner_email", "E-mail de login do responsável", "email"],
];

const blank = () => ({ company_name: "", owner_name: "", phone: "", owner_email: "", owner_password: generatePassword() });

export const CredentialsBox = ({ email, password, phone }) => {
  const text = `Acesso ao Ciclo Certo\nEndereço: ${window.location.origin}\nE-mail: ${email}\nSenha: ${password}`;
  const hasPhone = (phone || "").replace(/\D/g, "").length >= 10;
  const copy = () => navigator.clipboard.writeText(text).then(() => toast.success("Dados de acesso copiados.")).catch(() => toast.error("Não foi possível copiar."));
  return (
    <div className="space-y-3" data-testid="credentials-box">
      <pre className="whitespace-pre-wrap rounded-lg border border-slate-700 bg-slate-950 p-3 font-mono text-xs text-slate-200">{text}</pre>
      <Button onClick={copy} className="w-full rounded-full" data-testid="credentials-copy-button"><Copy className="mr-2 h-4 w-4" /> Copiar para enviar à empresa</Button>
      {hasPhone ? (
        <Button asChild className="w-full rounded-full bg-[#25D366] text-slate-950 hover:bg-[#1fb857]" data-testid="credentials-whatsapp-button">
          <a href={whatsappLink(phone, `Olá! Seguem seus dados de acesso ao Ciclo Certo (7 dias grátis):\n\n${text}`)} target="_blank" rel="noreferrer">
            <MessageCircle className="mr-2 h-4 w-4" /> Enviar pelo WhatsApp ({phone})
          </a>
        </Button>
      ) : <p className="text-center text-xs text-slate-500" data-testid="credentials-no-phone">Cadastre o telefone da empresa para enviar direto pelo WhatsApp.</p>}
    </div>
  );
};

export const NewCompanyDialog = ({ open, onClose, onCreated }) => {
  const [f, setF] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const close = () => { setF(blank()); setCreated(null); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/platform/companies", { ...f, responsible_name: f.owner_name });
      setCreated({ email: data.owner_email, password: f.owner_password, name: data.company_name, phone: f.phone });
      onCreated();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-slate-900 sm:max-w-md" data-testid="new-company-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">{created ? `${created.name} criada` : "Nova empresa"}</DialogTitle>
          <DialogDescription>{created ? "Envie os dados de acesso para o responsável." : "A empresa começa vazia e só vê os próprios dados."}</DialogDescription>
        </DialogHeader>
        {created ? <CredentialsBox email={created.email} password={created.password} phone={created.phone} /> : (
          <form onSubmit={submit} className="space-y-3">
            {FIELDS.map(([k, label, type]) => (
              <div key={k} className="space-y-1.5">
                <Label htmlFor={`nc_${k}`}>{label}</Label>
                <Input id={`nc_${k}`} type={type} required={k !== "phone"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="bg-slate-950" data-testid={`new-company-${k.replace("_", "-")}-input`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label htmlFor="nc_password">Senha inicial</Label>
              <div className="flex gap-2">
                <Input id="nc_password" required minLength={6} value={f.owner_password} onChange={(e) => setF({ ...f, owner_password: e.target.value })} className="bg-slate-950 font-mono" data-testid="new-company-password-input" />
                <Button type="button" variant="outline" className="border-slate-700 bg-transparent" onClick={() => setF({ ...f, owner_password: generatePassword() })} title="Gerar senha" data-testid="new-company-generate-password"><KeyRound className="h-4 w-4" /></Button>
              </div>
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-full font-semibold" data-testid="new-company-submit">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Criar empresa
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
